import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {View, StyleSheet, ScrollView, RefreshControl} from 'react-native';
import {
  Text, Appbar, Card, Button, ActivityIndicator,
  Portal, Dialog, IconButton, Menu, TextInput, Switch, Snackbar,
} from 'react-native-paper';
import {useNavigation, useRoute} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {customerApi} from '../../services/api/customerApi';
import {useConfigStore} from '../../store/useConfigStore';

const DEFAULT_PAYMENT_MODES = [
  {label: 'Cash', value: 'CASH'},
  {label: 'Bank Transfer', value: 'BANK_TRANSFER'},
  {label: 'Card Swipe', value: 'CARD_SWIPE'},
  {label: 'UPI', value: 'UPI'},
  {label: 'Other', value: 'OTHER'},
];

const CustomerDuesScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, customerName} = route.params;
  const {getProperty, fetchByType} = useConfigStore();

  const PAYMENT_MODES = useMemo(() => {
    const configured = getProperty('PAYMENT', 'PAYMENT_MODES');
    if (configured) {
      return configured.split(',').map(m => m.trim()).filter(Boolean).map(m => ({
        label: m,
        value: m.toUpperCase().replace(/\s+/g, '_'),
      }));
    }
    return DEFAULT_PAYMENT_MODES;
  }, [getProperty]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dueOrders, setDueOrders] = useState([]);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});

  // Payment form state
  const [paymentRows, setPaymentRows] = useState([]);
  const [adjustToZero, setAdjustToZero] = useState(false);
  const [addToCredits, setAddToCredits] = useState(false);

  const totalDue = useMemo(
    () => dueOrders.reduce((sum, o) => sum + Number(o.balanceAmount || 0), 0),
    [dueOrders],
  );

  const fetchDueOrders = useCallback(async () => {
    try {
      const res = await customerApi.getDueOrders(customerId);
      setDueOrders(res.data?.data || []);
    } catch (err) {
      setSnackbar({visible: true, message: err.response?.data?.message || 'Failed to load due orders'});
    }
  }, [customerId]);

  useEffect(() => {
    fetchByType('PAYMENT');
    setLoading(true);
    fetchDueOrders().finally(() => setLoading(false));
  }, [fetchDueOrders, fetchByType]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchDueOrders();
    setRefreshing(false);
  }, [fetchDueOrders]);

  const openPayment = useCallback(() => {
    setPaymentRows([{paymentMode: 'CASH', referenceNumber: '', amount: String(totalDue), menuVisible: false}]);
    setAdjustToZero(false);
    setAddToCredits(false);
    setPaymentVisible(true);
  }, [totalDue]);

  const totalBeingPaid = useMemo(() => {
    const sum = paymentRows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    return sum;
  }, [paymentRows]);

  const currentBalance = useMemo(() => totalDue - totalBeingPaid, [totalDue, totalBeingPaid]);

  const roundingAdjustment = useMemo(() => {
    if (!adjustToZero) return 0;
    return currentBalance > 0 ? currentBalance : 0;
  }, [adjustToZero, currentBalance]);

  const finalBalance = useMemo(() => currentBalance - roundingAdjustment, [currentBalance, roundingAdjustment]);

  const showAddToCredits = finalBalance < 0;

  const addRow = useCallback(() => {
    setPaymentRows(prev => [...prev, {paymentMode: 'CASH', referenceNumber: '', amount: '', menuVisible: false}]);
  }, []);

  const removeRow = useCallback(index => {
    setPaymentRows(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateRow = useCallback((index, field, value) => {
    setPaymentRows(prev => prev.map((r, i) => i === index ? {...r, [field]: value} : r));
  }, []);

  const getModeLabel = value => PAYMENT_MODES.find(m => m.value === value)?.label || value;

  const handleSubmit = async () => {
    const payments = paymentRows
      .filter(r => Number(r.amount) > 0)
      .map(r => ({
        amount: Number(r.amount),
        paymentMode: r.paymentMode,
        referenceNumber: r.referenceNumber || null,
      }));

    if (payments.length === 0) {
      setSnackbar({visible: true, message: 'Enter at least one payment amount'});
      return;
    }

    setSubmitting(true);
    try {
      const res = await customerApi.createBulkPayment(customerId, {
        customerId,
        payments,
        roundingAdjustment: roundingAdjustment > 0 ? roundingAdjustment : null,
        addToCredits: showAddToCredits ? addToCredits : false,
      });
      const data = res.data?.data;
      setPaymentVisible(false);
      setSnackbar({
        visible: true,
        message: `Payment recorded — ${data?.ordersPaid || 0} orders paid, ${data?.ordersPartiallyPaid || 0} partially paid`,
      });
      await fetchDueOrders();
    } catch (error) {
      setSnackbar({visible: true, message: error.response?.data?.message || 'Payment failed'});
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
          <Appbar.Content title="Due Orders" titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content
          title="Due Orders"
          subtitle={customerName}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
      </Appbar.Header>

      <ScrollView
        style={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {dueOrders.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No due orders</Text>
          </View>
        ) : (
          dueOrders.map(order => (
            <Card key={order.orderId} style={styles.orderCard}>
              <Card.Content style={styles.orderContent}>
                <View style={styles.orderLeft}>
                  <Text style={styles.orderNumber}>{order.orderNumber}</Text>
                  <Text style={styles.orderDate}>{order.orderDate}</Text>
                </View>
                <View style={styles.orderRight}>
                  {Number(order.balanceAmount) !== Number(order.totalAmount) && (
                    <Text style={styles.totalAmountStrike}>
                      {'\u20B9'}{Number(order.totalAmount).toLocaleString('en-IN')}
                    </Text>
                  )}
                  <Text style={styles.balanceValue}>
                    {'\u20B9'}{Number(order.balanceAmount).toLocaleString('en-IN')}
                  </Text>
                  <Text style={[styles.statusBadge, {
                    color: order.paymentStatus === 'UNPAID' ? colors.error : colors.warning,
                  }]}>
                    {order.paymentStatus === 'PARTIALLY_PAID' ? 'Partial' : 'Unpaid'}
                  </Text>
                </View>
              </Card.Content>
            </Card>
          ))
        )}
      </ScrollView>

      {dueOrders.length > 0 && (
        <View style={styles.bottomBar}>
          <View>
            <Text style={styles.totalLabel}>Total Due</Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{totalDue.toLocaleString('en-IN')}
            </Text>
          </View>
          <Button
            mode="contained"
            onPress={openPayment}
            style={styles.payButton}
            labelStyle={styles.payButtonLabel}>
            Pay
          </Button>
        </View>
      )}

      {/* Payment Modal */}
      <Portal>
        <Dialog visible={paymentVisible} onDismiss={() => setPaymentVisible(false)} style={styles.dialog}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>Bulk Payment</Text>
            <IconButton icon="close" size={20} onPress={() => setPaymentVisible(false)} />
          </View>
          <Dialog.Content>
            <ScrollView style={styles.scrollArea} showsVerticalScrollIndicator={false}>
              {/* Summary */}
              <View style={styles.summarySection}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Total Due ({dueOrders.length} orders)</Text>
                  <Text style={[styles.summaryValue, {color: colors.error}]}>
                    {'\u20B9'}{totalDue.toLocaleString('en-IN')}
                  </Text>
                </View>
              </View>

              {/* Payment Rows */}
              <Text style={styles.subTitle}>Payment</Text>
              {paymentRows.map((row, index) => (
                <View key={index} style={styles.paymentRow}>
                  <Menu
                    visible={row.menuVisible}
                    onDismiss={() => updateRow(index, 'menuVisible', false)}
                    anchor={
                      <Button
                        mode="outlined"
                        compact
                        onPress={() => updateRow(index, 'menuVisible', true)}
                        style={styles.modeButton}
                        labelStyle={styles.modeButtonLabel}>
                        {getModeLabel(row.paymentMode)}
                      </Button>
                    }>
                    {PAYMENT_MODES.map(mode => (
                      <Menu.Item
                        key={mode.value}
                        title={mode.label}
                        onPress={() => {
                          updateRow(index, 'paymentMode', mode.value);
                          updateRow(index, 'menuVisible', false);
                        }}
                      />
                    ))}
                  </Menu>
                  <TextInput
                    mode="outlined"
                    placeholder="Txn #"
                    value={row.referenceNumber}
                    onChangeText={v => updateRow(index, 'referenceNumber', v)}
                    style={styles.refInput}
                    dense
                  />
                  <TextInput
                    mode="outlined"
                    placeholder="Amount"
                    value={row.amount}
                    onChangeText={v => updateRow(index, 'amount', v)}
                    keyboardType="numeric"
                    style={styles.amountInput}
                    dense
                  />
                  {paymentRows.length > 1 && (
                    <IconButton icon="close-circle-outline" size={18} iconColor={colors.error}
                      onPress={() => removeRow(index)} style={{margin: 0}} />
                  )}
                </View>
              ))}

              <Button
                mode="text"
                icon="plus"
                compact
                onPress={addRow}
                style={styles.addButton}
                labelStyle={{fontSize: 13}}>
                Add Payment
              </Button>

              {/* Adjust to zero */}
              <View style={styles.switchRow}>
                <Text style={styles.switchLabel}>Adjust balance to zero?</Text>
                <Switch value={adjustToZero} onValueChange={setAdjustToZero} color={colors.primary} />
              </View>
              {adjustToZero && roundingAdjustment > 0 && (
                <Text style={styles.adjustText}>
                  Rounding adjustment: {'\u20B9'}{roundingAdjustment.toLocaleString('en-IN')}
                </Text>
              )}

              {/* Add to credits */}
              {showAddToCredits && (
                <View style={styles.switchRow}>
                  <Text style={styles.switchLabel}>Add excess to customer credits?</Text>
                  <Switch value={addToCredits} onValueChange={setAddToCredits} color={colors.primary} />
                </View>
              )}

              {/* Live Balance */}
              <View style={styles.balanceRow}>
                <Text style={styles.balanceLabel}>Remaining Balance:</Text>
                <Text style={[styles.balanceValue, {color: finalBalance <= 0 ? colors.success : colors.error}]}>
                  {'\u20B9'}{Math.abs(finalBalance).toLocaleString('en-IN')}
                  {finalBalance < 0 ? ' (overpaid)' : ''}
                </Text>
              </View>
            </ScrollView>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setPaymentVisible(false)} textColor={colors.textSecondary}>Cancel</Button>
            <Button
              mode="contained"
              onPress={handleSubmit}
              loading={submitting}
              disabled={submitting}
              style={styles.submitBtn}>
              PAY
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  appbarSubtitle: {color: colors.white, opacity: 0.8},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {flex: 1},
  emptyContainer: {flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60},
  emptyText: {color: colors.textSecondary, fontSize: 16},
  orderCard: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 10,
  },
  orderContent: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'},
  orderLeft: {flex: 1},
  orderNumber: {fontSize: 15, fontWeight: '600', color: colors.text},
  orderDate: {fontSize: 12, color: colors.textSecondary, marginTop: 2},
  orderRight: {alignItems: 'flex-end'},
  totalAmountStrike: {fontSize: 12, color: colors.textSecondary, textDecorationLine: 'line-through', marginBottom: 1},
  balanceValue: {fontSize: 16, fontWeight: '700', color: colors.error},
  statusBadge: {fontSize: 11, fontWeight: '600', marginTop: 2},
  bottomBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  totalLabel: {fontSize: 12, color: colors.textSecondary},
  totalValue: {fontSize: 20, fontWeight: '700', color: colors.error},
  payButton: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: spacing.lg},
  payButtonLabel: {fontSize: 16, fontWeight: '700'},
  // Modal styles
  dialog: {maxHeight: '85%', borderRadius: 15},
  titleRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingLeft: spacing.lg, paddingRight: spacing.xs,
  },
  title: {fontSize: 18, fontWeight: '700', color: colors.text},
  scrollArea: {maxHeight: 450},
  summarySection: {marginBottom: spacing.md},
  summaryRow: {flexDirection: 'row', justifyContent: 'space-between', marginBottom: spacing.xs},
  summaryLabel: {fontSize: 14, color: colors.text},
  summaryValue: {fontSize: 14, fontWeight: '600', color: colors.text},
  subTitle: {fontSize: 14, fontWeight: '700', color: colors.text, marginBottom: spacing.sm},
  paymentRow: {flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm, gap: 4},
  modeButton: {borderColor: colors.border, minWidth: 80},
  modeButtonLabel: {fontSize: 11},
  refInput: {flex: 1, fontSize: 13, height: 36},
  amountInput: {width: 80, fontSize: 13, height: 36},
  addButton: {alignSelf: 'flex-start', marginBottom: spacing.sm},
  switchRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.sm,
  },
  switchLabel: {fontSize: 13, color: colors.text, flex: 1},
  adjustText: {fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm, marginLeft: spacing.xs},
  balanceRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border, marginTop: spacing.sm,
  },
  balanceLabel: {fontSize: 15, fontWeight: '700', color: colors.text},
  balanceValue: {fontSize: 16, fontWeight: '700'},
  submitBtn: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10, width: 80, height: 36},
});

export default CustomerDuesScreen;
